import React, {useEffect, useState} from 'react';

import {fetchFeedbacks} from './api';
import {readOwnReview} from './cookie';
import ReviewItem from './components/ReviewItem';
import ReviewForm from './components/ReviewForm';

const VISIBLE_FIRST_PAGE = 15;

// Reviews page (`/reviews`) React app. Replaces the legacy
// `templates/reviews.phtml` server-rendered list + jQuery form +
// service-worker-cached page. Same markup classes ride the existing
// `redesign.css` rules, so visual parity is "free" — only the
// fetching / form behaviour moves to React.
//
// Behaviour parity with legacy:
//   · `?rate=N` URL param filters the visible list to N-star rows
//     (matches legacy hash-based filter rendered server-side).
//   · First 15 items render eagerly; the rest get `js-hidden-item`
//     until the customer hits "Load more".
//   · Customer's own review (from `rev_user` cookie) is prepended on
//     top and persists across reloads, exactly like the legacy PHP
//     `$_COOKIE['rev_user']` block.
//   · Submit-success prepends the new review locally AND removes the
//     form (legacy `.testimonial-form-sidebar` removal).
export default function ReviewsPage() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [ownReview, setOwnReview] = useState(() => readOwnReview());

    useEffect(() => {
        let cancelled = false;
        fetchFeedbacks()
            .then((rows) => { if (!cancelled) { setFeedbacks(rows); setLoading(false); } })
            .catch(() => { if (!cancelled) { setLoadError(true); setLoading(false); } });
        return () => { cancelled = true; };
    }, []);

    // Star-filter from URL `?rate=N`. Captured on first paint so the
    // chosen filter survives without reading `location` per render.
    const rateFilter = (() => {
        if (typeof window === 'undefined') return 0;
        const v = new URLSearchParams(window.location.search).get('rate');
        const n = parseInt(v, 10);
        return n >= 1 && n <= 5 ? n : 0;
    })();

    const visibleFeedbacks = rateFilter
        ? feedbacks.filter((f) => Math.round(Number(f.rate) || 0) === rateFilter)
        : feedbacks;

    return (
        <main className="page testimonials wrap">
            <div className="page-content text">
                <div className="page-titles">
                    <h1>What do our customers think?</h1>
                    <span className="page-title--subtitle">
                        We ask each customer to give us honest feedback because there is always
                        a place for improvement. If you want to find out an impartial opinion on
                        our work, you are welcome to read recent testimonials from our customers.
                    </span>
                </div>

                <div className="testimonials-items js-hidden-items-container">
                    {ownReview && <ReviewItem feedback={ownReview}/>}

                    {loading && (
                        <p style={{color: '#999', padding: '20px 0'}}>Loading reviews…</p>
                    )}
                    {loadError && (
                        <p style={{color: '#c62828', padding: '20px 0'}}>
                            Couldn’t load reviews. Refresh the page to try again.
                        </p>
                    )}

                    {visibleFeedbacks.map((feedback, idx) => (
                        <ReviewItem
                            key={`${feedback.writer?.id || 'w'}-${idx}`}
                            feedback={feedback}
                            hidden={!showAll && idx >= VISIBLE_FIRST_PAGE}
                        />
                    ))}
                </div>

                {!loading && visibleFeedbacks.length > VISIBLE_FIRST_PAGE && !showAll && (
                    <div className="js-load-more writers-item__hire">
                        <a
                            href="javascript:void(0);"
                            style={{width: 120}}
                            onClick={(e) => {e.preventDefault(); setShowAll(true);}}
                        >Load more</a>
                    </div>
                )}
            </div>

            <aside className="sidebar">
                {!ownReview && (
                    <ReviewForm onPublished={(payload) => setOwnReview(payload)}/>
                )}
                <div className="sidebar-item advantages-sidebar">
                    <div className="advantages-sidebar__item">
                        <img src="/_core/assets/themes/bidding/assets/images/check_green.svg" alt="check_green"/>
                        Plagiarism Free
                    </div>
                    <div className="advantages-sidebar__item">
                        <img src="/_core/assets/themes/bidding/assets/images/check_green.svg" alt="check_green"/>
                        Money Back Guarantee
                    </div>
                    <div className="advantages-sidebar__item">
                        <img src="/_core/assets/themes/bidding/assets/images/check_green.svg" alt="check_green"/>
                        100% Quality
                    </div>
                </div>
            </aside>

            <div className="page__waves">
                <div className="page__wave"/>
                <div className="page__wave2"/>
            </div>
        </main>
    );
}
